<template>
    <RegisterStep
        :ready="ready"
        :loading="loading"
        :previous-route="previousRoute"
        :order-step="orderStep"
        :show-submit="!isEmpty"
        v-bind="$attrs"
        @submit="handleStepSubmitted"
    >
        <template v-if="!isEmpty">
            <div class="mb-4">
                <div class="list-group" style="max-width: 500px">
                    <template v-for="registrant in sortedRegistrants">
                        <div class="list-group-item">
                            <div class="row align-items-center">
                                <div class="col">
                                    <div>
                                        <strong>
                                            {{ registrant.first_name }} {{ registrant.last_name }}
                                        </strong>
                                        <template v-if="registrant.self">
                                            <span class="text-muted">
                                                (moi-même)
                                            </span>
                                        </template>
                                    </div>
                                    <template v-if="registrant.email">
                                        <div class="small text-muted">{{ registrant.email }}</div>
                                    </template>
                                </div>
                                <template v-if="!registrant.self">
                                    <div class="col-auto">
                                        <button class="btn btn-outline-primary border-0 btn-sm" @click="handleEditClicked(registrant)">
                                            Modifier...
                                        </button>
                                        <button class="btn btn-outline-danger border-0 btn-sm" @click="handleDeleteClicked(registrant)">
                                            Supprimer
                                        </button>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </template>

        <Field name="registrants" :errors="errors">
            <div class="row mx-n2">
                <div class="col-auto px-2">
                    <button class="btn btn-primary" @click="handleAddClicked">
                        <Icon class="align-baseline mr-1" name="plus" width=".75em" height=".75em" />
                        Ajouter un inscrit
                    </button>
                </div>
            </div>
        </Field>

        <b-modal
            data-legacy
            :visible="!!editingRegistrant"
            dialog-class="cursus-register__form-modal"
            hide-footer
            @hide="editingRegistrant = null"
        >
            <template v-slot:modal-title>
                <template v-if="editingRegistrant && editingRegistrant.id">
                    Édition d’un inscrit
                </template>
                <template v-else>
                    Nouvel inscrit
                </template>
            </template>
            <template v-slot:default="{ visible, hide }">
                <template v-if="visible">
                    <RegistrantForm :value="editingRegistrant" :errors="errors" @submit="handleRegistrantSubmitted">
                        <template v-slot:footer>
                            <ModalFormFooter
                                :loading="loading"
                                :submit-label="editingRegistrant && editingRegistrant.id ? 'Modifier' : 'Ajouter'"
                                large
                                @cancel="hide"
                            />
                        </template>
                    </RegistrantForm>
                </template>
            </template>
        </b-modal>
    </RegisterStep>
</template>

<script>
    import { clone } from "../../../util";
    import Field from "../../Field.vue";
    import { deleteOrderRegistrant, postOrderRegistrant, postOrderThirdParties } from "../../../api";
    import RegistrantForm from "./registrants/RegistrantForm.vue";
    import ModalFormFooter from "../../ModalFormFooter.vue";
    import RegisterStep from "../RegisterStep.vue";
    import Icon from "../../ui/Icon.vue";
    import { showConfirm } from "../../../util/dialogs";
    import { nextStep, registerSteps, sortRegistrants } from "../../../util/register";

    export default {
        components: {
            Icon,
            RegisterStep,
            ModalFormFooter,
            RegistrantForm,
            Field
        },
        props: {
            order: Object,
            course: Object,
            session: Object,
            previousRoute: Object,
            orderStep: String,
        },
        data() {
            return {
                ready: false,
                currentOrder: clone({
                    ...this.order,
                    registrants: this.order.registrants ?? [],
                }),
                editingRegistrant: null,
                errors: null,
                loading: false,
            }
        },
        computed: {
            isEmpty() {
                return !this.currentOrder.registrants?.length;
            },
            sortedRegistrants() {
                return sortRegistrants(this.currentOrder.registrants);
            },
        },
        methods: {
            handleStepSubmitted() {
                const { courseId, sessionId, orderId } = this.$route.params;
                this.loading = true;
                this.errors = null;

                this.nextStep = this.getNextStep();
                this.nextRoute = this.nextStep.route;

                postOrderThirdParties({
                    courseId,
                    sessionId,
                    orderId,
                    data: {
                        ...this.currentOrder,
                        next_step: this.nextStep?.value,
                    },
                })
                .then(order => {
                    this.currentOrder = order;
                    this.$emit('update:order', order);
                    this.$router.push(this.nextRoute);
                })
                .catch(error => {
                    if(error.response?.status === 422) {
                        this.errors = error.response.data.errors;
                    } else {
                        return Promise.reject(error);
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
            },
            handleAddClicked() {
                this.editingRegistrant = {};
                this.errors = null;
            },
            handleEditClicked(registrant) {
                this.editingRegistrant = {
                    ...registrant,
                };
            },
            async handleRegistrantSubmitted(registrant) {
                const { courseId, sessionId, orderId } = this.$route.params;
                this.currentOrder = await postOrderRegistrant({
                    courseId, sessionId, orderId, registrantId: registrant.id,
                    data: registrant,
                })
                .catch(error => {
                    if(error.response.status === 422) {
                        this.errors = error.response.data.errors;
                    }
                    return Promise.reject(error);
                });
                this.editingRegistrant = null;
            },
            async handleDeleteClicked(registrant) {
                const confirmed = await showConfirm(this, `Supprimer ${registrant.first_name} ${registrant.last_name} des inscrits ?`, {
                    okTitle: 'Supprimer',
                    okVariant: 'danger',
                });
                if(!confirmed) {
                    return;
                }
                const { courseId, sessionId, orderId } = this.$route.params;
                this.currentOrder = await deleteOrderRegistrant({
                    courseId, sessionId, orderId, registrantId: registrant.id,
                });
            },
            async handleRegisterSelfClicked() {
                const { courseId, sessionId, orderId } = this.$route.params;
                this.currentOrder = await postOrderRegistrant({
                    courseId, sessionId, orderId,
                    data: {
                        self: true,
                    }
                });
            },
            getNextStep() {
                const steps = registerSteps({
                    order: this.currentOrder,
                    course: this.course,
                    session: this.session,
                });

                const currentRegistrantsCount = this.currentOrder.registrants?.length ?? 0;
                const oldRegistrantsCount = this.order.registrants?.length ?? 0;

                // more registrants than before: ignore current order step
                if(currentRegistrantsCount > oldRegistrantsCount) {
                    return nextStep(steps, this.$route);
                }

                return nextStep(steps, this.$route, this.orderStep);
            },
            init() {
                this.ready = true;
            },
        },
        created() {
            this.init();
        },
    }
</script>
